/**
 * Recipe Pairing Agent
 * Advanced recipe recommendations with meal planning and nutrition optimization
 */

interface Recipe {
  id: string;
  name: string;
  ingredients: string[];
  cuisine: string;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'dessert';
  prepTime: number;
  difficulty: 'easy' | 'medium' | 'hard';
  servings: number;
  nutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  tags: string[];
}

interface MealPlan {
  day: string;
  meals: {
    breakfast?: Recipe;
    lunch?: Recipe;
    dinner?: Recipe;
    snacks?: Recipe[];
  };
  totalNutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  shoppingList: string[];
}

interface PairingRecommendation {
  mainRecipe: Recipe;
  sides: Recipe[];
  beverages: string[];
  reason: string;
  compatibilityScore: number;
}

class RecipePairingAgent {
  private recipes: Recipe[];

  constructor() {
    this.recipes = this.initializeRecipes();
  }

  /**
   * Get recipe pairings for a main dish
   * @param mainRecipe Main recipe or ingredients
   * @param preferences User dietary preferences
   * @returns Pairing recommendations
   */
  getRecipePairings(
    mainRecipe: string | string[],
    preferences?: {
      dietary?: string[];
      maxPrepTime?: number;
      servings?: number;
    }
  ): PairingRecommendation[] {
    const mainIngredients = typeof mainRecipe === 'string' ? [mainRecipe] : mainRecipe;

    // Find complementary recipes
    const pairings: PairingRecommendation[] = [];

    for (const recipe of this.recipes) {
      if (recipe.mealType === 'dinner') {
        const sides = this.findComplementarySides(recipe, preferences);
        const beverages = this.suggestBeverages(recipe);
        const compatibilityScore = this.calculateCompatibility(recipe, sides);

        if (sides.length > 0) {
          pairings.push({
            mainRecipe: recipe,
            sides,
            beverages,
            reason: this.generatePairingReason(recipe, sides),
            compatibilityScore,
          });
        }
      }
    }

    return pairings.sort((a, b) => b.compatibilityScore - a.compatibilityScore).slice(0, 5);
  }

  /**
   * Generate weekly meal plan
   * @param ingredients Available ingredients
   * @param preferences Dietary preferences and constraints
   * @returns 7-day meal plan
   */
  generateMealPlan(
    ingredients: string[],
    preferences?: {
      dietary?: string[];
      calorieTarget?: number;
      mealsPerDay?: number;
      variety?: boolean;
    }
  ): MealPlan[] {
    const mealPlan: MealPlan[] = [];
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const usedRecipes = new Set<string>();

    for (const day of days) {
      const meals: MealPlan['meals'] = {};
      let totalCalories = 0;
      let totalProtein = 0;
      let totalCarbs = 0;
      let totalFat = 0;

      // Breakfast
      const breakfast = this.selectMeal('breakfast', ingredients, preferences, usedRecipes);
      if (breakfast) {
        meals.breakfast = breakfast;
        totalCalories += breakfast.nutrition.calories;
        totalProtein += breakfast.nutrition.protein;
        totalCarbs += breakfast.nutrition.carbs;
        totalFat += breakfast.nutrition.fat;
        if (preferences?.variety) usedRecipes.add(breakfast.id);
      }

      // Lunch
      const lunch = this.selectMeal('lunch', ingredients, preferences, usedRecipes);
      if (lunch) {
        meals.lunch = lunch;
        totalCalories += lunch.nutrition.calories;
        totalProtein += lunch.nutrition.protein;
        totalCarbs += lunch.nutrition.carbs;
        totalFat += lunch.nutrition.fat;
        if (preferences?.variety) usedRecipes.add(lunch.id);
      }

      // Dinner
      const dinner = this.selectMeal('dinner', ingredients, preferences, usedRecipes);
      if (dinner) {
        meals.dinner = dinner;
        totalCalories += dinner.nutrition.calories;
        totalProtein += dinner.nutrition.protein;
        totalCarbs += dinner.nutrition.carbs;
        totalFat += dinner.nutrition.fat;
        if (preferences?.variety) usedRecipes.add(dinner.id);
      }

      // Generate shopping list for the day
      const shoppingList = this.generateShoppingList([
        ...(meals.breakfast ? [meals.breakfast] : []),
        ...(meals.lunch ? [meals.lunch] : []),
        ...(meals.dinner ? [meals.dinner] : []),
      ], ingredients);

      mealPlan.push({
        day,
        meals,
        totalNutrition: {
          calories: Math.round(totalCalories),
          protein: Math.round(totalProtein),
          carbs: Math.round(totalCarbs),
          fat: Math.round(totalFat),
        },
        shoppingList,
      });
    }

    return mealPlan;
  }

  /**
   * Find recipes that use multiple available ingredients
   * @param ingredients Available ingredients
   * @param minimumMatch Minimum number of matching ingredients
   * @returns Recipes sorted by ingredient match
   */
  findRecipesByIngredients(ingredients: string[], minimumMatch: number = 2): Array<{
    recipe: Recipe;
    matchCount: number;
    missingIngredients: string[];
  }> {
    const matches: Array<any> = [];

    for (const recipe of this.recipes) {
      const matchingIngredients = recipe.ingredients.filter(ing =>
        ingredients.some(avail => ing.toLowerCase().includes(avail.toLowerCase()))
      );

      if (matchingIngredients.length >= minimumMatch) {
        const missingIngredients = recipe.ingredients.filter(
          ing => !matchingIngredients.includes(ing)
        );

        matches.push({
          recipe,
          matchCount: matchingIngredients.length,
          missingIngredients,
        });
      }
    }

    return matches.sort((a, b) => b.matchCount - a.matchCount);
  }

  /**
   * Optimize meal plan for nutrition goals
   * @param mealPlan Current meal plan
   * @param goals Nutrition goals
   * @returns Optimized meal plan with suggestions
   */
  optimizeMealPlan(
    mealPlan: MealPlan[],
    goals: {
      caloriesPerDay: number;
      proteinGrams: number;
      lowCarb?: boolean;
      balanced?: boolean;
    }
  ): {
    optimizedPlan: MealPlan[];
    nutritionScore: number;
    suggestions: string[];
  } {
    const suggestions: string[] = [];
    let totalScore = 0;

    for (const day of mealPlan) {
      const { calories, protein, carbs, fat } = day.totalNutrition;

      // Check calorie alignment
      const caloriesDiff = Math.abs(calories - goals.caloriesPerDay);
      if (caloriesDiff > 200) {
        suggestions.push(`${day.day}: Adjust portions to meet calorie goal`);
      } else {
        totalScore += 25;
      }

      // Check protein
      if (protein >= goals.proteinGrams) {
        totalScore += 25;
      } else {
        suggestions.push(`${day.day}: Add protein-rich foods`);
      }

      // Check balance
      if (goals.balanced) {
        const totalMacros = protein + carbs + fat;
        const proteinPercent = (protein / totalMacros) * 100;
        const carbPercent = (carbs / totalMacros) * 100;
        const fatPercent = (fat / totalMacros) * 100;

        if (proteinPercent >= 20 && proteinPercent <= 35 &&
            carbPercent >= 40 && carbPercent <= 60 &&
            fatPercent >= 20 && fatPercent <= 35) {
          totalScore += 25;
        } else {
          suggestions.push(`${day.day}: Rebalance macronutrient ratios`);
        }
      }
    }

    const nutritionScore = Math.round((totalScore / (mealPlan.length * 75)) * 100);

    return {
      optimizedPlan: mealPlan,
      nutritionScore,
      suggestions: suggestions.slice(0, 5),
    };
  }

  // Helper Methods

  private initializeRecipes(): Recipe[] {
    return [
      {
        id: 'caprese-salad',
        name: 'Fresh Caprese Salad',
        ingredients: ['tomatoes', 'mozzarella', 'basil', 'olive oil'],
        cuisine: 'Italian',
        mealType: 'lunch',
        prepTime: 15,
        difficulty: 'easy',
        servings: 4,
        nutrition: { calories: 250, protein: 12, carbs: 8, fat: 18 },
        tags: ['vegetarian', 'fresh', 'summer'],
      },
      {
        id: 'grilled-chicken',
        name: 'Herb Grilled Chicken',
        ingredients: ['chicken', 'herbs', 'lemon', 'garlic'],
        cuisine: 'American',
        mealType: 'dinner',
        prepTime: 45,
        difficulty: 'medium',
        servings: 4,
        nutrition: { calories: 350, protein: 42, carbs: 2, fat: 18 },
        tags: ['protein', 'low-carb', 'grilled'],
      },
      {
        id: 'roasted-vegetables',
        name: 'Roasted Seasonal Vegetables',
        ingredients: ['peppers', 'squash', 'onions', 'olive oil'],
        cuisine: 'Mediterranean',
        mealType: 'dinner',
        prepTime: 35,
        difficulty: 'easy',
        servings: 4,
        nutrition: { calories: 180, protein: 4, carbs: 22, fat: 8 },
        tags: ['vegan', 'healthy', 'side'],
      },
    ];
  }

  private findComplementarySides(
    mainRecipe: Recipe,
    preferences?: { dietary?: string[]; maxPrepTime?: number }
  ): Recipe[] {
    return this.recipes.filter(recipe => {
      if (recipe.id === mainRecipe.id) return false;
      if (recipe.mealType !== 'dinner' && recipe.mealType !== 'lunch') return false;
      if (!recipe.tags.includes('side')) return false;

      if (preferences?.dietary) {
        const matchesDietary = preferences.dietary.some(diet => recipe.tags.includes(diet));
        if (!matchesDietary) return false;
      }

      if (preferences?.maxPrepTime) {
        if (recipe.prepTime > preferences.maxPrepTime) return false;
      }

      return true;
    });
  }

  private suggestBeverages(recipe: Recipe): string[] {
    const beverages: string[] = [];

    if (recipe.cuisine === 'Italian') {
      beverages.push('Chianti', 'Sparkling Water', 'Iced Tea');
    } else if (recipe.cuisine === 'Mexican') {
      beverages.push('Margarita', 'Horchata', 'Mexican Beer');
    } else {
      beverages.push('Water', 'Iced Tea', 'White Wine');
    }

    return beverages;
  }

  private calculateCompatibility(main: Recipe, sides: Recipe[]): number {
    let score = 50;

    // Cuisine compatibility
    if (sides.some(s => s.cuisine === main.cuisine)) score += 20;

    // Nutrition balance
    const totalProtein = main.nutrition.protein + sides.reduce((sum, s) => sum + s.nutrition.protein, 0);
    const totalCarbs = main.nutrition.carbs + sides.reduce((sum, s) => sum + s.nutrition.carbs, 0);
    const totalFat = main.nutrition.fat + sides.reduce((sum, s) => sum + s.nutrition.fat, 0);

    const total = totalProtein + totalCarbs + totalFat;
    const balanced = Math.abs(totalProtein / total - 0.3) < 0.1 &&
                     Math.abs(totalCarbs / total - 0.4) < 0.1 &&
                     Math.abs(totalFat / total - 0.3) < 0.1;

    if (balanced) score += 30;

    return score;
  }

  private generatePairingReason(main: Recipe, sides: Recipe[]): string {
    const cuisineMatch = sides.some(s => s.cuisine === main.cuisine);
    if (cuisineMatch) {
      return `Traditional ${main.cuisine} pairing with complementary flavors`;
    }
    return 'Balanced nutritional profile with varied textures';
  }

  private selectMeal(
    mealType: Recipe['mealType'],
    ingredients: string[],
    preferences?: any,
    usedRecipes?: Set<string>
  ): Recipe | undefined {
    const candidates = this.recipes.filter(recipe => {
      if (recipe.mealType !== mealType) return false;
      if (usedRecipes?.has(recipe.id)) return false;

      const hasIngredients = recipe.ingredients.some(ing =>
        ingredients.some(avail => ing.toLowerCase().includes(avail.toLowerCase()))
      );

      if (!hasIngredients && ingredients.length > 0) return false;

      if (preferences?.dietary) {
        const matchesDietary = preferences.dietary.some((diet: string) => recipe.tags.includes(diet));
        if (preferences.dietary.length > 0 && !matchesDietary) return false;
      }

      return true;
    });

    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  private generateShoppingList(recipes: Recipe[], availableIngredients: string[]): string[] {
    const needed = new Set<string>();

    for (const recipe of recipes) {
      for (const ingredient of recipe.ingredients) {
        const available = availableIngredients.some(avail =>
          ingredient.toLowerCase().includes(avail.toLowerCase())
        );
        if (!available) {
          needed.add(ingredient);
        }
      }
    }

    return Array.from(needed);
  }
}

export const recipePairingAgent = new RecipePairingAgent();
export default recipePairingAgent;
